/**
 * Shared, lazily-built AudioBuffers and PeriodicWaves. Every buffer is
 * rendered once per context and reused by every voice.
 */
import { brownNoise, fourierSeries, impulseTrain, mulberry32, normalizeRms, pinkNoise, seamless, whiteNoise, type Rng } from './dsp';
import { REVERB_PROFILES } from './mapping';
import * as S from './samples';
import type { RideBiome } from '../biomes';

type Channels = Float32Array | Float32Array[];

const hash = (key: string) => { let h = 2166136261; for (let i = 0; i < key.length; i++) h = Math.imul(h ^ key.charCodeAt(i), 16777619); return h >>> 0; };

export class SoundKit {
  readonly sr: number;
  /** Sample rate for slow control signals (8 kHz where supported). */
  readonly controlRate: number;
  private buffers = new Map<string, AudioBuffer>();
  private waveCache = new Map<string, PeriodicWave>();
  private queue: (() => void)[] = [];

  constructor(readonly ctx: BaseAudioContext, private seed = 1) {
    this.sr = ctx.sampleRate;
    let rate = this.sr;
    try { ctx.createBuffer(1, 8, 8000); rate = 8000; } catch { /* older engines need the context rate */ }
    this.controlRate = rate;
  }
  rng(key: string): Rng { return mulberry32(hash(key) ^ this.seed); }

  /** Cached buffer; `render` receives the sample rate it must render at. */
  buffer(key: string, render: (sr: number, rng: Rng) => Channels, rate = this.sr): AudioBuffer {
    const cached = this.buffers.get(key);
    if (cached) return cached;
    const data = render(rate, this.rng(key));
    const channels = Array.isArray(data) ? data : [data];
    let buffer: AudioBuffer;
    try { buffer = this.ctx.createBuffer(channels.length, channels[0].length, rate); }
    catch { buffer = this.ctx.createBuffer(channels.length, channels[0].length, this.sr); }
    channels.forEach((d, i) => buffer.getChannelData(i).set(d));
    this.buffers.set(key, buffer);
    return buffer;
  }
  /** Queue buffers to render in idle slices. */
  defer(...jobs: (() => void)[]) { this.queue.push(...jobs); }
  /** Render queued buffers until `budgetMs` is spent. Returns jobs remaining. */
  prewarm(budgetMs = 8): number {
    const start = now();
    while (this.queue.length && now() - start < budgetMs) this.queue.shift()!();
    return this.queue.length;
  }

  // ---- continuous sources
  get white() { return this.buffer('white', (sr, rng) => normalizeRms(seamless(whiteNoise(Math.round(sr * 6.2), rng), 256), .3)); }
  get pink() { return this.buffer('pink', (sr, rng) => normalizeRms(seamless(pinkNoise(Math.round(sr * 6.6), rng), 512), .3)); }
  get brown() { return this.buffer('brown', (sr, rng) => normalizeRms(seamless(brownNoise(Math.round(sr * 5.8), rng), 2048), .3)); }
  get gravelSparse() { return this.buffer('gravel-sparse', (sr, rng) => S.gravelLoop(sr, 3.31, 26, rng)); }
  get gravelDense() { return this.buffer('gravel-dense', (sr, rng) => S.gravelLoop(sr, 2.93, 190, rng)); }
  get grit() { return this.buffer('grit', (sr, rng) => S.gritLoop(sr, 2.71, rng)); }
  get loam() { return this.buffer('loam', (sr, rng) => S.loamLoop(sr, 3.13, rng)); }
  get spray() { return this.buffer('spray', (sr, rng) => S.sprayLoop(sr, 2.37, rng)); }
  /** Slow gusts (~0.7 control points per second). */
  get gust() { return this.buffer('gust', (sr, rng) => S.modulation(sr, 23.7, .7, rng), this.controlRate); }
  /** Fast terrain texture (~9 bumps per second). */
  get texture() { return this.buffer('texture', (sr, rng) => S.modulation(sr, 6.1, 9, rng), this.controlRate); }
  get swell() { return this.buffer('swell', (sr, rng) => S.surfSwell(sr, 41, rng), this.controlRate); }
  get babble() { return this.buffer('babble', (sr, rng) => S.babble(sr, 7.3, 14, rng), this.controlRate); }

  // ---- one-shots
  pebble(i: number) { return this.buffer(`pebble${i % 4}`, S.pebble); }
  clatter(i: number) { return this.buffer(`clatter${i % 3}`, S.metalClatter); }
  thud(i: number) { return this.buffer(`thud${i % 2}`, S.bodyThud); }
  scatter(i: number) { return this.buffer(`scatter${i % 2}`, (sr, rng) => S.gravelScatter(sr, 1.4, rng)); }
  scrape(i: number) { return this.buffer(`scrape${i % 2}`, (sr, rng) => S.scrape(sr, .9, rng, 1700 + i % 2 * 500)); }
  spin(engagement: number) { return this.buffer(`spin${engagement}`, (sr, rng) => S.wheelSpinTail(sr, 3, rng, engagement)); }
  get thump() { return this.buffer('thump', S.suspensionThump); }
  slap(i: number) { return this.buffer(`slap${i % 3}`, S.tyreSlap); }
  chain(i: number) { return this.buffer(`chain${i % 2}`, S.chainSlap); }
  get pop() { return this.buffer('pop', S.takeoffPop); }
  get knock() { return this.buffer('knock', S.knock); }
  swish(i: number) { return this.buffer(`swish${i % 2}`, S.swish); }
  get exhale() { return this.buffer('exhale', S.exhale); }
  chime(f1: number, f2: number) { return this.buffer(`chime${Math.round(f1)}`, (sr, rng) => S.gateChime(sr, f1, f2, rng)); }
  get tick() { return this.buffer('tick', sr => S.countdownTick(sr, 880)); }
  get go() { return this.buffer('go', S.goChord); }
  get cheer() { return this.buffer('cheer', (sr, rng) => S.crowdCheer(sr, 4.5, rng)); }
  get fanfare() { return this.buffer('fanfare', S.fanfare); }
  bird(species: S.BirdSpecies, i: number) { return this.buffer(`bird-${species}${i % 3}`, (sr, rng) => S.birdCall(sr, species, rng)); }
  gull(i: number) { return this.buffer(`gull${i % 4}`, S.gullCall); }
  hawk(i: number) { return this.buffer(`hawk${i % 2}`, S.hawkCall); }
  bee(i: number) { return this.buffer(`bee${i % 2}`, S.beeFlyby); }
  waterSlap(i: number) { return this.buffer(`water${i % 4}`, S.waterSlap); }
  mast(i: number) { return this.buffer(`mast${i % 3}`, (sr, rng) => S.mastClink(sr, [910, 1160, 1430][i % 3], rng)); }
  cowbell(i: number) { return this.buffer(`cowbell${i % 3}`, S.cowbell); }
  pa(i: number) { return this.buffer(`pa${i % 3}`, S.paPhrase, Math.max(22050, Math.round(this.sr / 2))); }
  get foghorn() { return this.buffer('foghorn', S.foghorn, Math.max(22050, Math.round(this.sr / 2))); }
  get music() { return this.buffer('music', S.festivalLoop, Math.max(22050, Math.round(this.sr / 2))); }
  impulse(biome: RideBiome) { return this.buffer(`ir-${biome}`, (sr, rng) => S.impulseResponse(sr, REVERB_PROFILES[biome], rng)); }

  // ---- periodic waves
  wave(key: 'impulse' | 'tread' | 'link' | 'squeal' | 'inhale' | 'exhale' | 'cicada' | 'cricket'): PeriodicWave {
    const cached = this.waveCache.get(key);
    if (cached) return cached;
    let wave: PeriodicWave;
    const c = this.ctx;
    if (key === 'impulse') { const w = impulseTrain(1024); wave = c.createPeriodicWave(w.real, w.imag, { disableNormalization: true }); }
    else if (key === 'tread') { const w = impulseTrain(16, .22); wave = c.createPeriodicWave(w.real, w.imag); }
    else if (key === 'link') { const w = impulseTrain(8, .35); wave = c.createPeriodicWave(w.real, w.imag); }
    else if (key === 'cicada') { const w = impulseTrain(6, .3); wave = c.createPeriodicWave(w.real, w.imag); }
    else if (key === 'squeal') { wave = c.createPeriodicWave(new Float32Array([0, 1, .32, .14, .05]), new Float32Array(5)); }
    else {
      const env = ENVELOPES[key];
      const w = fourierSeries(env, key === 'cricket' ? 96 : 24);
      wave = c.createPeriodicWave(w.real, w.imag, { disableNormalization: true });
    }
    this.waveCache.set(key, wave);
    return wave;
  }
  /** Mean of a periodic envelope: the base gain to add under its oscillator. */
  envelopeMean(key: keyof typeof ENVELOPES): number { return fourierSeries(ENVELOPES[key], 1, 512).mean; }
}

/** One-cycle envelopes used as audio-rate LFO shapes. */
export const ENVELOPES = {
  inhale: (u: number) => (u < .36 ? Math.sin(Math.PI * u / .36) ** 1.6 * .55 : 0),
  exhale: (u: number) => { const w = (u - .4) / .55; return w < 0 || w > 1 ? 0 : w < .14 ? w / .14 : Math.exp(-(w - .14) * 3.4) * (1 - w) ** .3; },
  cricket: (u: number) => { if (u > .32) return 0; const p = u / .32 * 3; const f = p - Math.floor(p); return Math.sin(Math.PI * Math.min(1, f / .7)) ** 2 * (f < .7 ? 1 : 0); },
};

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
